// src/components/student/StudentDashboardShell.js
//
// Warm light-themed student dashboard — matches sas_dashboard_v3.html mockup.
// Wraps existing data loading from StudentDashboard.
// Layout: welcome strip, streak banner, stats row, next action card,
// coaching trends, development goals, progress bars + XP, activity feed.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import StudentNav from '../shared/StudentNav';
import StudentOrgBar from './StudentOrgBar';
import { useStudentReviews } from '../../hooks/useStudentReviews';
import { scopeToOrganization } from '../../utils/studentDataAccess';
import { buildMySkillsPath } from '../../utils/mySkillsPath';
import { useStudentContext } from '../../hooks/useStudentContext';
import { useStudentModernVideos } from '../../hooks/useStudentModernData';
import MotusIcon from '../shared/MotusIcon';

// ── Light theme tokens ──
const C = {
  bg:'#FAFAF8', card:'#FFFFFF', card2:'#F5F4F0', card3:'#ECEAE4',
  border:'rgba(0,0,0,0.06)', borderH:'rgba(0,0,0,0.12)',
  blue:'#2563EB', blueDim:'rgba(37,99,235,0.08)',
  green:'#22C55E', greenDim:'rgba(34,197,94,0.08)',
  orange:'#F05A1F', orangeDim:'rgba(240,90,31,0.08)',
  purple:'#9333EA', purpleDim:'rgba(147,51,234,0.08)',
  gold:'#D97706', goldDim:'rgba(217,119,6,0.08)',
  red:'#DC2626',
  t1:'#1C1B18', t2:'#6B6B63', t3:'#A3A299',
};
const font = "'DM Sans',sans-serif";
const fontD = "'Bebas Neue',sans-serif";

function StudentDashboardShell({ legacyDashboard }) {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const studentName = userData?.name || currentUser?.displayName || 'Student';
  const studentId = currentUser?.uid || '';

  // Pass 85.1A.1 — CANONICAL participant/org context. MODERN account: video stats come from the org+participant scoped
  // hook (no uid-only read, no stale flash). Its coaching evidence is safe published lessons / reviewed videos only —
  // NOT clientNotes (staff CRM), so the review card is an honest limited state until safe modern review evidence exists.
  // Pass 85.1A.2 — the CANONICAL ACCESS LANE decides data mode. clientNotes (legacy reviews) and the uid video read run
  // ONLY when lane === 'legacy' (a SUCCESSFUL lookup that returned ZERO participant profiles). Every UNRESOLVED canonical
  // state (loading / error / needs_org / ambiguous) passes EMPTY inputs → no clientNotes read, no uid query, no fallback.
  const ctx = useStudentContext(studentId);
  const lane = ctx.lane;
  const isModern = lane === 'modern';
  const isLegacy = lane === 'legacy';
  const mv = useStudentModernVideos(isModern ? studentId : '', isModern ? ctx.selectedOrganizationId : '', isModern ? ctx.activeParticipantId : '');

  // LEGACY reviews (clientNotes) — the hook receives a real uid/org ONLY in the confirmed-legacy lane; empty otherwise.
  const legacyOrgId = userData?.orgId || '';
  const { reviews: legacyReviews } = useStudentReviews(isLegacy ? studentId : '', isLegacy ? legacyOrgId : '', 3);
  const reviews = isLegacy ? legacyReviews : [];

  // LEGACY video stats (uid + in-memory org) — runs ONLY for a confirmed-legacy account.
  const [legacyStats, setLegacyStats] = useState({ total:0, reviewed:0, inReview:0, drills:0 });
  const [legacyVideoError, setLegacyVideoError] = useState(false);
  useEffect(() => {
    if (lane !== 'legacy' || !studentId) return undefined;
    let alive = true;
    setLegacyVideoError(false);
    (async () => {
      try {
        const snap = await getDocs(query(collection(db,'videos'), where('userId','==',studentId)));
        const vids = scopeToOrganization(snap.docs.map(d => ({ id:d.id, ...d.data() })), legacyOrgId);
        if (alive) setLegacyStats({ total: vids.length, reviewed: vids.filter(v => v.status === 'reviewed').length, inReview: vids.filter(v => v.status === 'in_review' || v.status === 'pending').length, drills: 0 });
      } catch(_) { if (alive) setLegacyVideoError(true); }
    })();
    return () => { alive = false; };
  }, [lane, studentId, legacyOrgId]);

  const videoStats = isModern
    ? { total: mv.videos.length, reviewed: mv.videos.filter(v => v.status === 'reviewed').length, inReview: mv.videos.filter(v => v.status === 'in_review' || v.status === 'pending').length, drills: 0 }
    : (isLegacy ? legacyStats : { total:0, reviewed:0, inReview:0, drills:0 });
  const videoLoadError = lane === 'error' || (isModern ? !!mv.error : (isLegacy ? legacyVideoError : false));

  // Latest review for "next action" card (legacy only; modern shows an honest limited state)
  const latestReview = reviews[0] || null;

  return (
    <div style={{ minHeight:'100vh', background:C.bg, color:C.t1, fontFamily:font }}>
      {/* BG */}
      <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0, background:'radial-gradient(ellipse 60% 40% at 5% 0%,rgba(37,99,235,0.03),transparent 55%),radial-gradient(ellipse 45% 35% at 95% 95%,rgba(240,90,31,0.02),transparent 55%)' }} />

      <StudentNav activeTab="dashboard" />

      <main style={{ padding:'36px 36px 80px', maxWidth:1260, margin:'0 auto', position:'relative', zIndex:1 }} data-student-mode={lane}>

        <StudentOrgBar ctx={ctx} />

        {lane === 'loading' && (
          <div data-testid="dashboard-lane-loading" style={{ padding:'12px 0', color:'#A3A299', fontSize:13 }}>Resolving your coaching profile…</div>
        )}

        {videoLoadError && (
          <div data-testid="dashboard-video-load-error" style={{ background:'#FEF2F2', border:'1px solid #FECACA', color:'#B91C1C', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:13 }}>
            We couldn’t load your videos right now — these counts may be incomplete. This is a loading problem, not an empty library.
          </div>
        )}

        {/* WELCOME */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:58, height:58, borderRadius:'50%', background:`linear-gradient(135deg,${C.blue},${C.purple})`, border:'2px solid rgba(37,99,235,0.25)', boxShadow:'0 0 22px rgba(37,99,235,0.12)', display:'flex', alignItems:'center', justifyContent:'center' }}><MotusIcon name="skiPoles" size={24} color="#FFFFFF" /></div>
            <div>
              <div style={{ fontFamily:fontD, fontSize:28, letterSpacing:1.5, color:C.t1, lineHeight:1, marginBottom:5 }}>Welcome Back, {studentName.split(' ')[0]}</div>
              <div style={{ fontSize:13, color:C.t3 }}>Your coaching, videos, and progress in one place</div>
            </div>
          </div>
        </div>

        {/* STATS — canonical, video-/review-derived facts only */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:22 }}>
          <StatCard label="Total Uploads" value={videoStats.total} color={C.blue} accent={`linear-gradient(90deg,${C.blue},${C.purple})`} sub="Videos submitted" />
          <StatCard label="Reviewed" value={videoStats.reviewed} color={C.green} accent={`linear-gradient(90deg,${C.green},#16A34A)`} sub="Feedback received" />
          <StatCard label="In Review" value={videoStats.inReview} color={C.orange} accent={`linear-gradient(90deg,${C.orange},#CC4A10)`} sub="Awaiting coach" />
          <StatCard label="Coach Reviews" value={reviews.length} color={C.gold} accent={`linear-gradient(90deg,${C.gold},#FBBF24)`} sub="Reviews received" />
        </div>

        {/* NEXT ACTION CARD */}
        <div onClick={() => navigate(latestReview ? '/me/videos' : '/me/upload')} style={{ background:'linear-gradient(135deg,#EFF6FF,#F0F4FF)', border:'1px solid rgba(37,99,235,0.12)', borderRadius:18, padding:24, display:'flex', alignItems:'center', gap:24, marginBottom:18, cursor:'pointer', position:'relative', overflow:'hidden', transition:'all .2s' }}>
          <div style={{ width:64, height:64, borderRadius:18, background:C.blueDim, border:'1px solid rgba(37,99,235,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><MotusIcon name="target" size={28} color={C.blue} /></div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:C.blue, marginBottom:4 }}>Recommended Next</div>
            <div style={{ fontFamily:fontD, fontSize:26, letterSpacing:1.5, color:C.t1, marginBottom:4 }}>
              {latestReview ? 'Your coach left you new feedback' : 'Upload a video to get started'}
            </div>
            <div style={{ fontSize:13, color:C.t2, lineHeight:1.5 }}>
              {latestReview ? `"${latestReview.noteText?.slice(0,80)}${latestReview.noteText?.length > 80 ? '…' : ''}"` : 'Get AI + coach feedback using the structured coaching framework.'}
            </div>
          </div>
          <button className="m-btn m-btn-primary" style={{ whiteSpace:'nowrap' }}>
            {latestReview ? 'View Feedback →' : 'Upload Video →'}
          </button>
        </div>

        {/* BOTTOM GRID */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18 }}>

          {/* YOUR PROGRESS — canonical teaser → /me/progress (no fabricated trends/XP) */}
          <div onClick={() => navigate(buildMySkillsPath())} style={{ gridColumn:'1/-1', background:C.card, border:`1px solid ${C.border}`, borderRadius:18, padding:24, cursor:'pointer', display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
            <div style={{ width:56, height:56, borderRadius:16, background:C.blueDim, border:'1px solid rgba(37,99,235,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><MotusIcon name="target" size={26} color={C.blue} /></div>
            <div style={{ flex:1, minWidth:220 }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:C.blue, marginBottom:4 }}>Your Progress</div>
              <div style={{ fontFamily:fontD, fontSize:24, letterSpacing:1.5, color:C.t1, marginBottom:3 }}>Focus, Level &amp; Next Step</div>
              <div style={{ fontSize:13, color:C.t3, lineHeight:1.5 }}>See what to work on, what you&rsquo;ve demonstrated, and your next step — always tied to real coach review and lesson evidence.</div>
            </div>
            <button className="m-btn m-btn-primary" style={{ whiteSpace:'nowrap' }}>View My Progress →</button>
          </div>

          {/* SEASON PROGRESS — canonical video/review facts only */}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:18, padding:24 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:C.blue, marginBottom:4 }}>Your Journey</div>
            <div style={{ fontFamily:fontD, fontSize:24, letterSpacing:1.5, color:C.t1, marginBottom:14 }}>Season Progress</div>

            <ProgBar label="Videos Reviewed" count={`${videoStats.reviewed} / ${videoStats.total}`} pct={videoStats.total > 0 ? (videoStats.reviewed/videoStats.total)*100 : 0} color={`linear-gradient(90deg,${C.blue},${C.purple})`} />
            <ProgBar label="Coach Reviews" count={`${reviews.length}`} pct={Math.min(100, reviews.length * 20)} color={`linear-gradient(90deg,${C.orange},#CC4A10)`} last />
          </div>

          {/* NEXT UP — simple canonical actions */}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:18, padding:24 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:C.blue, marginBottom:4 }}>Next Steps</div>
            <div style={{ fontFamily:fontD, fontSize:24, letterSpacing:1.5, color:C.t1, marginBottom:14 }}>Keep Going</div>
            <div onClick={() => navigate('/me/upload')} style={{ display:'flex', gap:13, padding:'14px 0', borderBottom:`1px solid ${C.border}`, alignItems:'flex-start', cursor:'pointer' }}>
              <div style={{ width:9, height:9, borderRadius:'50%', background:C.orange, marginTop:5, flexShrink:0 }} />
              <div><div style={{ fontSize:14, color:C.t1, lineHeight:1.5 }}>Upload a coaching clip</div><div style={{ fontSize:11, color:C.t3, marginTop:3 }}>Get coach feedback on your riding</div></div>
            </div>
            <div onClick={() => navigate(buildMySkillsPath())} style={{ display:'flex', gap:13, padding:'14px 0', alignItems:'flex-start', cursor:'pointer' }}>
              <div style={{ width:9, height:9, borderRadius:'50%', background:C.blue, marginTop:5, flexShrink:0 }} />
              <div><div style={{ fontSize:14, color:C.t1, lineHeight:1.5 }}>Review your progression</div><div style={{ fontSize:11, color:C.t3, marginTop:3 }}>Your focus, level, and next step</div></div>
            </div>
          </div>

          {/* ACTIVITY FEED — full width */}
          <div style={{ gridColumn:'1/-1', background:C.card, border:`1px solid ${C.border}`, borderRadius:18, padding:24 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:C.blue, marginBottom:4 }}>Activity</div>
            <div style={{ fontFamily:fontD, fontSize:24, letterSpacing:1.5, color:C.t1, marginBottom:4 }}>Recent Activity</div>
            <div style={{ fontSize:13, color:C.t3, lineHeight:1.5, marginBottom:18 }}>Your latest interactions across the platform.</div>

            {reviews.length > 0 ? reviews.map((r,i) => (
              <FeedItem key={r.id} icon={<MotusIcon name="check" size={18} color={C.green} />} iconBg={C.greenDim} iconBorder="rgba(34,197,94,0.18)" text={`Coach review: "${r.noteText?.slice(0,60)}${r.noteText?.length > 60 ? '…' : ''}"`} meta={fmtDate(r.createdAt)} badge="Feedback" badgeBg={C.greenDim} badgeBorder="rgba(34,197,94,0.18)" badgeColor={C.green} last={i === reviews.length - 1} />
            )) : (
              <div style={{ padding:'20px 0', textAlign:'center', fontSize:13, color:C.t3 }}>No activity yet — upload a video or complete a session to get started</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ── Sub-components ──

function StatCard({ label, value, color, accent, sub }) {
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:'20px 20px 16px', position:'relative', overflow:'hidden', transition:'all .2s', cursor:'default' }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:accent, borderRadius:'16px 16px 0 0' }} />
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:C.t3, marginBottom:10 }}>{label}</div>
      <div style={{ fontFamily:fontD, fontSize:50, lineHeight:1, letterSpacing:1, color }}>{value}</div>
      <div style={{ fontSize:12, color:C.t3, marginTop:6 }}>{sub}</div>
    </div>
  );
}

function ProgBar({ label, count, pct, color, last }) {
  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
        <span style={{ fontSize:13, fontWeight:500, color:C.t1 }}>{label}</span>
        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:12, color:C.t3 }}>{count}</span>
      </div>
      <div style={{ height:7, background:C.card3, borderRadius:7, overflow:'hidden', marginBottom: last ? 0 : 16 }}>
        <div style={{ height:'100%', borderRadius:7, background:color, width:`${Math.min(100,Math.max(0,pct))}%`, transition:'width 1s' }} />
      </div>
    </>
  );
}

function FeedItem({ icon, iconBg, iconBorder, text, meta, badge, badgeBg, badgeBorder, badgeColor, last }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom: last ? 'none' : `1px solid ${C.border}` }}>
      <div style={{ width:40, height:40, borderRadius:11, background:iconBg, border:`1px solid ${iconBorder}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>{icon}</div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.t1, marginBottom:2 }}>{text}</div>
        <div style={{ fontSize:11, color:C.t3 }}>{meta}</div>
      </div>
      <span style={{ fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:20, background:badgeBg, border:`1px solid ${badgeBorder}`, color:badgeColor, whiteSpace:'nowrap' }}>{badge}</span>
    </div>
  );
}

function fmtDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const diffH = (now - d) / (1000*60*60);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${Math.round(diffH)}h ago`;
  if (diffH < 48) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day:'numeric', month:'short' });
}

export default StudentDashboardShell;
